/**
 * アップロード画面。
 *
 * 【2経路（CLAUDE.md 原則4）】
 *   軽量ファイル（サムネ・ポスター） … 全デバイスのブラウザ → R2
 *   マスター                          … Box へ直接
 *
 * マスターの経路はファイルサイズで分かれる（Box の分割アップロードは20MB未満を
 * 受け付けないため）。詳細は lib/uploadRunner.ts。
 *
 * 【端末制限】
 * スマートフォンからの**動画**マスターは受け付けない。iOS Safari は画面を
 * 離れると接続を切るため、数GBの分割アップロードが途中で必ず壊れる。
 * 写真は一度に送り切れるので許可している（2026-08-19 判断）。
 *
 * 【なぜ1本ずつ流すのか】
 * 同時に走らせると上り帯域を取り合って全部が遅くなる。
 * 順に流せば、終わったものから使えるようになる。
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase, type Category } from '../lib/supabase.ts'
import { leafCategories, categoryPathLabel } from '../lib/categories.ts'
import { ApiError, type ImportResult } from '../lib/api.ts'
import { importAssetsFromBox } from '../lib/api.ts'
import { DropZone } from '../components/DropZone.tsx'
import {
  toQueueItems,
  queueSummary,
  formatBytes,
  isMobileDevice,
  type QueueItem,
} from '../lib/uploadQueue.ts'
import { runUpload } from '../lib/uploadRunner.ts'

export function Upload() {
  const [categories, setCategories] = useState<Category[]>([])
  const [categoryId, setCategoryId] = useState('')
  const [items, setItems] = useState<QueueItem[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [running, setRunning] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // 判定はマウント時に1回だけ。画面の途中で変わるものではない。
  const mobile = useMemo(() => isMobileDevice(), [])

  useEffect(() => {
    supabase
      .from('categories')
      .select('*')
      .order('sort_order')
      .then(({ data }) => {
        const rows = (data ?? []) as Category[]
        setCategories(rows)
        // 素材を置けるのは末端のカテゴリだけ
        const leaves = leafCategories(rows)
        if (leaves[0]) setCategoryId(leaves[0].id)
      })
  }, [])

  const summary = useMemo(() => queueSummary(items), [items])

  const addFiles = (files: File[]) => {
    setItems((prev) => [...prev, ...toQueueItems(files, mobile)])
  }

  const patch = (id: string, next: Partial<QueueItem>) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...next } : i)))

  /**
   * 待ち行列を順に流す。
   *
   * 状態は setItems の関数形で更新するが、**ループの制御には使えない**
   * （React の state はこのループ内では更新されない）。
   * 対象は開始時点のスナップショットから決める。
   */
  const start = async () => {
    if (!categoryId || running) return
    setRunning(true)
    setWarnings([])
    const controller = new AbortController()
    abortRef.current = controller

    // 開始時点で待機中のものだけを対象にする
    const targets = items.filter((i) => i.status === 'waiting' || i.status === 'failed')

    for (const item of targets) {
      if (controller.signal.aborted) break
      patch(item.id, { status: 'uploading', error: null, percent: null, uploadedBytes: 0 })
      try {
        const result = await runUpload({
          file: item.file,
          kind: item.kind,
          categoryId,
          signal: controller.signal,
          callbacks: {
            onProgress: (uploadedBytes, percent) => patch(item.id, { uploadedBytes, percent }),
            onWarning: (message) =>
              setWarnings((prev) => [...prev, `${item.file.name}: ${message}`]),
          },
        })
        patch(item.id, {
          status: 'succeeded',
          percent: 100,
          uploadedBytes: item.file.size,
          assetId: result.assetId,
        })
      } catch (e) {
        const message =
          e instanceof ApiError
            ? `${e.code}（${e.message}）`
            : e instanceof Error
              ? e.message
              : String(e)
        patch(item.id, { status: 'failed', error: message })
        // 1本の失敗で残りを止めない。1件ずつ独立して扱う。
      }
    }

    setRunning(false)
    abortRef.current = null
  }

  const cancel = () => {
    abortRef.current?.abort()
    setRunning(false)
  }

  const clearFinished = () =>
    setItems((prev) => prev.filter((i) => i.status !== 'succeeded' && i.status !== 'rejected'))

  const remove = (id: string) => setItems((prev) => prev.filter((i) => i.id !== id))

  const waiting = items.filter((i) => i.status === 'waiting' || i.status === 'failed').length

  return (
    <div className="upload pad">
      <BoxImporter />

      <h2>アップロード</h2>

      <label htmlFor="category">カテゴリ</label>
      <select
        id="category"
        value={categoryId}
        onChange={(e) => setCategoryId(e.target.value)}
        disabled={running}
      >
        {leafCategories(categories).map((c) => (
          <option key={c.id} value={c.id}>
            {categoryPathLabel(categories, c.id)}
          </option>
        ))}
      </select>
      <p className="muted small">
        選んだカテゴリに対応する Box フォルダへ入ります（素材を置けるのは末端のカテゴリだけです）。
        あとから移動もできます。
      </p>

      <DropZone onFiles={addFiles} disabled={running} allowVideo={!mobile} />

      {items.length > 0 && (
        <div className="queue">
          <div className="queue-head">
            <span className="small">
              {items.length} 件（完了 {summary.done}／失敗 {summary.failed}
              {summary.rejected > 0 ? `／対象外 ${summary.rejected}` : ''}）
            </span>
            <div className="queue-actions">
              {running ? (
                <button onClick={cancel}>中止する</button>
              ) : (
                <button className="primary" disabled={waiting === 0 || !categoryId} onClick={start}>
                  {waiting} 件をアップロード
                </button>
              )}
              {!running && (summary.done > 0 || summary.rejected > 0) && (
                <button className="link" onClick={clearFinished}>
                  完了・対象外を消す
                </button>
              )}
            </div>
          </div>

          {running && (
            <div className="tbar">
              <div className="tbar-fill" style={{ width: `${summary.percent}%` }} />
            </div>
          )}

          <ul className="queue-list">
            {items.map((item) => (
              <li key={item.id} className={`qitem q-${item.status}`}>
                <div className="qitem-main">
                  <span className="qitem-name">{item.file.name}</span>
                  <span className="muted small">
                    {formatBytes(item.file.size)} ・ {item.kind === 'video' ? '動画' : '写真'}
                  </span>
                </div>
                <div className="qitem-state">
                  {item.status === 'rejected' && <span className="error small">{item.rejection}</span>}
                  {item.status === 'waiting' && <span className="muted small">待機中</span>}
                  {item.status === 'uploading' && (
                    <div className="qitem-progress">
                      <span className="small">
                        {item.percent === null ? '送信中…' : `${item.percent}%`}
                      </span>
                      <div className={`tbar${item.percent === null ? ' indeterminate' : ''}`}>
                        <div
                          className="tbar-fill"
                          style={item.percent === null ? undefined : { width: `${item.percent}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {item.status === 'succeeded' && <span className="success small">完了</span>}
                  {item.status === 'failed' && <span className="error small">{item.error}</span>}
                  {!running && item.status !== 'uploading' && (
                    <button className="link small" onClick={() => remove(item.id)}>
                      取り除く
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="warnings">
          <p className="small">
            <strong>アップロードは成功していますが、付随処理で問題が出ました:</strong>
          </p>
          <ul>
            {warnings.map((w, i) => (
              <li key={i} className="muted small">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Box に直接置かれたファイルを取り込む。
 *
 * Box Drive 経由で大容量マスターを入れる運用があるため、
 * ブラウザからのアップロードを経ずにカタログへ載せる経路が必要になる。
 */
function BoxImporter() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(await importAssetsFromBox())
    } catch (e) {
      setError(e instanceof ApiError ? `${e.code}（${e.message}）` : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <details className="importer">
      <summary>Box に直接置いたファイルを取り込む</summary>
      <p className="muted small">
        Box Drive などでフォルダに直接置いたファイルを走査して、カタログに登録します。
        すでに登録済みのものは飛ばすので、何度実行しても増えません。
      </p>
      <button onClick={run} disabled={busy}>
        {busy ? '走査中…' : 'Box を走査する'}
      </button>
      {result && (
        <p className="small">
          新規 {result.imported} 件 ／ 既存 {result.skipped} 件
          {result.categories.length > 0 && (
            <span className="muted">
              {' '}
              （{result.categories.map((c) => `${c.categoryName} ${c.imported}`).join('、')}）
            </span>
          )}
        </p>
      )}
      {result && result.quarantined > 0 && (
        <p className="warn small">
          子カテゴリを持つカテゴリのフォルダにあった {result.quarantined} 件は「未分類」で登録しました。
          カタログの「未分類」から末端のカテゴリへ移してください（Box のファイルも一緒に移動します）。
        </p>
      )}
      {error && <p className="error small">{error}</p>}
    </details>
  )
}
